import { useEffect, useState } from "react";
import {
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    FormHelperText,
    Box,
    Divider,
    Button,
    FormControl,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Control, Controller, UseFormGetValues, UseFormReset, UseFormSetValue, useWatch } from "react-hook-form";
import {nanoid} from "nanoid";

import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
    ApplicableDocuments,
    CharacterCount,
    DocumentAttachements,
} from "../../../constants/DocumentUpload";
import {
    FiberManualRecordIconStyle,
    DocGridBody,
    ListItemStyle,
    DocumentTypeIcon,
    documentMargin,
    ButtonErrorWidth,
    ClearErrorButtonBox,
} from "../../PriorAuthWorkFlow/styles/style";
import { isEmpty } from "lodash";
import { flexAllCentre, flexRow } from "../../../styles/mui/styles/display";
import { ReAuthTabData } from "../../../models/TabSessionStorage";
import { extractDocType, getValue } from "../../../utils";
import FileUpload from "../../../components/FormPriorAuthReAuth/FileUpload";
import { rules } from "../../../utils/validation/Validation";
import { maxFileSize } from "../../../constants/Config";
import {
    DocumentError3,
    DocumentError4,
    DocumentError5,
} from "../../../constants/FunctionalUtils";
import { getDocTypeIcon } from "../../../utils/DocIcons";
import { Asterisk, RiDeleteIcon } from "../../../components/styles/styles";
import { TabSessionDataReAuthResponse } from "../../../models/api/TabSessionStorage";
import { updateTabStatus } from "../../../constants/TabSessionStorage";
import { FormInputText } from "../../../components/AuthFormComponents/FormInputText";
import { AuthTextLabel } from "../../../app/common/TextLabel";
import { InputHeader, NotesStyle, NotesTextFieldStyle } from "../styles/style";

export interface PropsFromState {
    setValue: UseFormSetValue<any>;
    control: Control<ReAuthTabData, any>;
    reset: UseFormReset<ReAuthTabData>;
    getValues: UseFormGetValues<ReAuthTabData>;
    tabSessionStorage: TabSessionDataReAuthResponse;
    setFileUpload: (files: File[]) => void;
    fileUpload: File[];
}

export interface PropsFromDispatch {
    resetStates: (actionType: string) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const DocumentUpload: React.FC<AllProps> = ({
    setFileUpload,
    fileUpload,
    control,
    setValue,
    getValues,
    reset,
}: AllProps) => {
    const [error, setError] = useState<any>();
    const [files, setFiles] = useState<File[]>([]);

    const [totalSize, setTotalSize] = useState<number>(0);
    const maxFileSizeInMegaBytes = maxFileSize;
    const maxFileSizeInBytes = Number(maxFileSizeInMegaBytes) * 1000000;
    const minFileSize = 1;

    useEffect(() => {
        if (files.length > 0) {
            const selectedFiles = files;
            if (!selectedFiles) return;
            let newTotalSize = totalSize;
            const newFiles: any[] = [];
            let errorMessage = null;

            Array.from(selectedFiles).forEach((file) => {
                const typedFile = file;
                const isDuplicate = fileUpload.some((existingFile: File) => {
                    return (
                        existingFile.name === typedFile.name &&
                        existingFile.size === typedFile.size
                    );
                });
                if (isDuplicate) {
                    errorMessage = `${DocumentError4} ${typedFile.name}`;
                    return;
                }

                if (typedFile.size === 0) {
                    errorMessage = `${typedFile.name} - ${DocumentError5}`;
                    return;
                }
                newTotalSize += typedFile.size;
                if (newTotalSize > maxFileSize) {
                    errorMessage = DocumentError3;
                    return;
                }
                newFiles.push(typedFile);
            });

            if (errorMessage) {
                setError(errorMessage);
                setFiles([]);
            } else {
                setFileUpload([...fileUpload, ...newFiles]);
                setFiles([]);
                setTotalSize(newTotalSize);
                setError(null);
            }
        }
    }, [files]);

    useEffect(() => {
        setValue(
            "documentUploads",
            getValue(fileUpload, "[0].name")
                ? fileUpload.map((file: File) => {
                    const entries = Object.entries(file).filter(
                        ([key]) => key !== "lastModifiedDate"
                    );
                    return Object.fromEntries(entries);
                })
                : []
        );
    }, [fileUpload]);

    const handleDelete = (fileName: string, fileSize: number) => {
        setFileUpload(fileUpload.filter((file: File) => file.name !== fileName));

        setError(null);
        const newTotalSize = Math.max(0, totalSize - fileSize);
        setTotalSize(newTotalSize);
    };

    useEffect(() => {
        reset({
            ...getValues(),
            reAuthTabStatus: updateTabStatus(4, getValues("reAuthTabStatus")),
        });
    }, []);

    const noteCommentValue = useWatch({
        control,
        name: "noteComment",
        defaultValue: getValues("noteComment") ?? "",
    });



    return (
        <>
            <Grid container pt={"1rem"} marginX={"2rem"}>
                <Grid size={{ xs: 6 }}>
                    <Typography
                        variant="h6"
                        fontWeight={fontWeight[5]}
                        color={colors.fonts[20]}
                    >
                        Please submit the following as attachments:
                    </Typography>
                    <List>
                        {DocumentAttachements.map((doc: any) => (
                            <ListItem key={doc}>
                                <ListItemIcon sx={ListItemStyle}>
                                    <FiberManualRecordIconStyle />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={fontWeight[3]}
                                            color={colors.black[13]}
                                        >
                                            {doc}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </Grid>
                <Grid size={{ xs: 6 }} justifyContent={"flex-end"}>
                    <Typography
                        variant="h6"
                        fontWeight={fontWeight[5]}
                        color={colors.fonts[20]}
                    >
                        Please provide the following, as applicable:
                    </Typography>
                    <List>
                        {ApplicableDocuments.map((doc: any) => (
                            <ListItem key={doc}>
                                <ListItemIcon sx={ListItemStyle}>
                                    <FiberManualRecordIconStyle />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={fontWeight[3]}
                                            color={colors.black[13]}
                                        >
                                            {doc}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </Grid>
            </Grid>
            <Grid container pt={"3rem"} sx={flexAllCentre}>
                <Grid size={{ xs: 8 }} sx={flexRow}>
                    <Typography fontWeight={fontWeight[3]} color={colors.fonts[2]}>
                        Document upload is mandatory
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        color={colors.red[90]}
                        fontWeight={fontWeight[4]}
                        pr={2}
                        sx={Asterisk}
                    >
                        *
                    </Typography>
                </Grid>
            </Grid>
            <Grid container pb={"0.1rem"} sx={flexAllCentre}>
                <Grid size={{ xs: 8 }} sx={DocGridBody}>
                    <Controller
                        name={"documentUploads.0.path"}
                        control={control}
                        defaultValue={""}
                        rules={rules.document}
                        render={({ fieldState: { error } }) => {
                            return (
                                <>
                                    <FileUpload
                                        files={files}
                                        setFiles={setFiles}
                                        maxFileSizeInBytes={maxFileSizeInBytes}
                                        minFileSize={minFileSize}
                                        type={1}
                                    />
                                    {error && fileUpload.length === 0 && (
                                        <FormHelperText>{error.message}</FormHelperText>
                                    )}
                                </>
                            );
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container pb={"2rem"} sx={flexAllCentre}>
                <Grid size={{ xs: 8 }} pl={4} sx={documentMargin}>
                    {fileUpload &&
                        fileUpload.map((data: any, index: number) => (
                            <>
                                <Grid container key={nanoid()} sx={flexRow} pt={"0.2rem"}>
                                    <Grid size={{ xs: 10 }} sx={flexRow}>
                                        <Box sx={DocumentTypeIcon}>
                                            {getDocTypeIcon(extractDocType(data.type))}
                                        </Box>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={fontWeight[4]}
                                            color={colors.fonts[4]}
                                            pt={1}
                                        >
                                            {data.name}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 2 }}>
                                        <RiDeleteIcon
                                            onClick={() => handleDelete(data.name, data.size)}
                                        />
                                    </Grid>
                                </Grid>
                                {!isEmpty(fileUpload) && fileUpload.length !== index + 1 && (
                                    <Divider
                                        sx={{ margin: "0 0.5rem 0.5rem 0", paddingTop: 1 }}
                                    />
                                )}
                            </>
                        ))}
                </Grid>
                <Grid size={{ xs: 8 }} pl={4} sx={documentMargin}>
                    {error && (
                        <Box
                            sx={ClearErrorButtonBox}
                        >
                            <FormHelperText error>{error}</FormHelperText>
                            <Button
                                onClick={() => setError(null)}
                                variant="contained"
                                sx={ButtonErrorWidth}
                            >
                                Clear Error
                            </Button>
                        </Box>
                    )}
                </Grid>
            </Grid>
            <Grid container pb={"2rem"} sx={flexAllCentre}>
                <Grid size={{ xs: 12 }} mt={3} sx={NotesStyle}>
                    <Grid size={{ xs: 12 }} mr={2.5} pr={1} sx={InputHeader}>
                        {AuthTextLabel("Notes:", false)}
                        <FormControl variant="standard">
                            <FormInputText
                                name="noteComment"
                                control={control}
                                style={NotesTextFieldStyle(false, true)}
                                textLength={250}
                                multiline={true}
                            />
                        </FormControl>
                        {CharacterCount(noteCommentValue ? noteCommentValue.length : 0)}
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default DocumentUpload;